import {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import Auth from "opensooq-sso";
import { EnumLoginState, ILoginState } from "../hooks/useAuthSSO";
import { IMember } from "../screens/AccountMembership/_private/types/midView";
import {sleep} from "utils/misc/fetchClient";

interface AuthSsoProviderProps {
  auth: Auth;
  loggedInState: ILoginState;
  userInfo: any;
}

type AuthContextType = {
  setIsLoggedInSso: Dispatch<SetStateAction<boolean>>;
  isLoggedInSso: boolean;
  verifiedToken: (cb?) => Promise<{ token: string, userInfo: IMember }>;
  loggedInToken: (cb?) => Promise<{ token: string, userInfo: IMember }>;
  logout: () => Promise<void>;
  genericToken: () => Promise<string>;
  authenticate: (url) => Promise<void>;
  auth: Auth;
  userInfo: IMember;
  showShimmer: boolean;
};

export const AuthSsoContext = createContext<AuthContextType>({
  verifiedToken: async () => undefined,
  loggedInToken: async () => undefined,
  logout: async () => {},
  genericToken: async () => "",
  authenticate: async () => {},
  setIsLoggedInSso: () => {},
  isLoggedInSso: undefined,
  auth: {},
  userInfo: undefined,
  showShimmer: undefined,
});

export function useSsoContext(): AuthContextType {
  return useContext(AuthSsoContext);
}

export const SSO_LOGIN_STATE = "ssoLoginState";
const AuthSsoProvider: FC<AuthSsoProviderProps> = ({
  children,
  auth,
  loggedInState,
  userInfo: _userInfo,
}) => {
  const [isLoggedInSso, setIsLoggedInSso] = useState<boolean>(
    loggedInState?.state === EnumLoginState.LoggedIn
  );

  const [userInfo, setUserInfo] = useState<IMember>(_userInfo);
  const [showShimmer, setShowShimmer] = useState<boolean>(true);

  useEffect(() => {

    if (loggedInState?.state === EnumLoginState.LoggedOut || _userInfo){
      setShowShimmer(false);
    }

    if (loggedInState?.state === EnumLoginState.LoggedIn) {
      setIsLoggedInSso(true);
      setUserInfo(_userInfo);
    }
  }, [loggedInState, _userInfo]);

// this event listener to set the userInfo after login from api
  useEffect(() => {
    const onSuccess = async () => {
      const userInfo = await getIdentity();
      setIsLoggedInSso(true);
      setUserInfo(userInfo);
    };
    document.addEventListener("on-login-success-api", onSuccess);
    return () => document.removeEventListener("on-login-success-api", onSuccess);
  }, []);



  const dispatchLogoutEvent = (loggedInState: boolean) => {
    const event = new CustomEvent("ssoLogout", { detail: { loggedInState } });
    document.dispatchEvent(event);
  };

  const getIdentity = async () => {
    const user = await Auth.getInstance().getIdentity();
    setUserInfo(user);
    return user;
  };
  const verifiedToken = async (onContinueCb = ()=> {}) => {
    while(!window[SSO_LOGIN_STATE]) {
      await sleep(5)
    }
    const token = await Auth.getInstance().getToken(true, true , onContinueCb);
    const identity = await getIdentity();
    setIsLoggedInSso(true);
    return {token , userInfo : identity};
  };

  const loggedInToken = async (onContinueCb = ()=> {}) : Promise<{ token: string, userInfo: IMember }> => {
    while(!window[SSO_LOGIN_STATE]) {
      await sleep(5)
    }
    const token = await Auth.getInstance().getToken(true ,false , onContinueCb);
    const identity = await getIdentity();
    setIsLoggedInSso(true);
    return {token , userInfo : identity};
  };

  const logout = async () => {
    await Auth.getInstance().destroy();
    setUserInfo(undefined)
    setIsLoggedInSso(false);
    dispatchLogoutEvent(false);
  };

  const genericToken = async () => {
    const token = await Auth.getInstance().getToken(false);
    return token;
  };

  const authenticate = async (url:string) => {
    await Auth.getInstance().authenticate(url , true);
  };

  return (
    <AuthSsoContext.Provider
      value={{
        isLoggedInSso,
        setIsLoggedInSso,
        verifiedToken,
        loggedInToken,
        logout,
        genericToken,
        authenticate,
        auth,
        userInfo,
        showShimmer,
      }}
    >
      {children}
    </AuthSsoContext.Provider>
  );
};

export default AuthSsoProvider;
